import { useEffect, useRef } from 'react';
import { subscribeSessionExpired } from './sessionExpiry';

/**
 * Detiene un proceso periódico cuando la sesión deja de servir, y opcionalmente lo reanuda si la
 * sesión se recupera (#168, #172).
 *
 * <p>Un poller no se entera solo: sigue tickeando detrás del aviso, pegándole a una API que le
 * responde 401 a todo. El incidente que originó esto fueron **9414 requests en 70 minutos** —unas
 * 2,2 por segundo, que no es alguien clickeando sino un `setInterval`—, y el criterio de aceptación
 * del arreglo es justamente que esa ráfaga desaparezca de los logs.
 *
 * <p><b>La reanudación no es un lujo.</b> `resetSessionExpiry` es un camino de primera clase: si una
 * renovación entra bien después de que un 401 marcara la sesión, el aviso se va solo y la app queda
 * sana. Sin reanudar, el usuario se queda con pantallas congeladas que ya nadie actualiza y sin nada
 * que se lo diga. Sólo se reanuda lo que este hook efectivamente detuvo.
 *
 * @param {() => void} detener corta el proceso.
 * @param {() => void} [reanudar] lo vuelve a arrancar. Omitirlo es válido cuando el proceso no se
 *        puede retomar solo — por ejemplo una firma masiva, donde la decisión es del usuario.
 */
export const useStopOnSessionExpired = (detener, reanudar) => {
  // Por ref para que funciones con identidad nueva en cada render no re-suscriban.
  const detenerRef = useRef(detener);
  detenerRef.current = detener;
  const reanudarRef = useRef(reanudar);
  reanudarRef.current = reanudar;
  const loDetuvimosRef = useRef(false);

  useEffect(() => subscribeSessionExpired((motivo) => {
    if (motivo) {
      if (loDetuvimosRef.current) return;
      loDetuvimosRef.current = true;
      detenerRef.current?.();
      return;
    }
    // Volvió a estar sana: sólo se reanuda lo que este hook detuvo.
    if (!loDetuvimosRef.current) return;
    loDetuvimosRef.current = false;
    reanudarRef.current?.();
  }), []);
};
